// pages/Company/CompanyCreatePage.jsx
// ─────────────────────────────────────────────────────────────
// REBUILT FROM ZERO — not an iteration on the previous 2-step form.
// Previous version still treated this as "fill out a form, just
// spread across two screens." This version removes ALL profile
// fields (RC number, address, website, contact info, low-balance
// threshold) from the creation flow entirely — none of it is
// load-bearing for using the product, so none of it belongs between
// the user and their first real action. Those fields now live
// EXCLUSIVELY in CompanySettingsPage.jsx, surfaced later via the
// profile-completion nudge (see CompanySettingsPage.jsx's
// ProfileCompletionCard) — completing them becomes something a user
// chooses to unlock, not a gate they're pushed through.
//
// INTERACTION PATTERN: the name input is embedded IN the sentence
// ("Let's set up payroll for ___") rather than a labeled box beside a
// question — the Stripe/Linear pattern where the input IS the
// sentence. Industry is a lightweight chip-select, not a text field —
// faster to answer and gives structured data instead of free text
// that would need normalizing later.
//
// On successful creation, navigates straight into
// CompanyOnboardingPage (the guided "what's next" map) instead of the
// raw dashboard — creation is the START of onboarding, not the end of
// a form.
//
// SECOND-COMPANY CASE: an `isAdditional` flag (passed via router
// state from the dashboard's "+ New" button) suppresses all of the
// framing copy and skips straight to a minimal name input + Create —
// a returning user creating company #2 already knows the product and
// shouldn't see onboarding marketing copy again.
// ─────────────────────────────────────────────────────────────
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast }       from "react-toastify";
import { apiClient }   from "../../api/apiServices";

const INDUSTRIES = [
  { value: "retail",     label: "Retail",     icon: "ri-store-2-line" },
  { value: "logistics",  label: "Logistics",  icon: "ri-truck-line" },
  { value: "technology", label: "Technology", icon: "ri-code-s-slash-line" },
  { value: "services",   label: "Services",   icon: "ri-customer-service-2-line" },
  { value: "hospitality",label: "Hospitality",icon: "ri-restaurant-line" },
  { value: "other",      label: "Other",      icon: "ri-more-line" },
];

export default function CompanyCreatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const isAdditional = !!location.state?.isAdditional;

  const [name, setName]         = useState("");
  const [industry, setIndustry] = useState(null);
  const [loading, setLoading]   = useState(false);

  const canCreate = name.trim().length >= 2;

  const create = async () => {
    if (!canCreate) return;
    setLoading(true);
    try {
      const r = await apiClient.post("/mtrade/companies", {
        name: name.trim(),
        industry: industry || undefined,
      });
      if (r.data.status) {
        toast.success("Company created");
        const companyId = r.data.data.companyId;
        if (isAdditional) {
          // Returning user, already knows the product — straight to
          // the dashboard, no onboarding map.
          navigate("/business");
        } else {
          navigate(`/business/${companyId}/welcome`, { state: { companyName: name.trim() } });
        }
      } else {
        toast.error(r.data.message);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not create company");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg flex flex-col font-display">
      <div className="px-5 pt-12 pb-4 flex-shrink-0">
        <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-xl bg-surface-hover flex items-center justify-center">
          <i className="ri-arrow-left-line text-secondary text-lg" />
        </button>
      </div>

      <div className="flex-1 flex flex-col justify-center px-6 pb-20 max-w-md mx-auto w-full">
        {!isAdditional && (
          <p className="text-muted text-sm font-semibold mb-2 tracking-wide uppercase">Step 1 of 1</p>
        )}

        {/* FIXED — the actual UX centerpiece: the input lives inside
            the sentence, not beside a separate question label. */}
        <h1 className="text-primary font-bold text-[26px] leading-snug mb-8">
          Let's set up payroll for{" "}
          <span className="inline-block relative" style={{ minWidth: "140px" }}>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="your company"
              autoFocus
              onKeyDown={e => e.key === "Enter" && canCreate && create()}
              className="bg-transparent border-b-[3px] outline-none w-full text-brand placeholder:text-faint"
              style={{ borderColor: name ? "var(--color-brand)" : "var(--color-border-strong)" }}
            />
          </span>
        </h1>

        {!isAdditional && (
          <div className="mb-10">
            <p className="text-secondary text-sm font-semibold mb-3">What kind of business is it? <span className="text-faint font-normal">(optional)</span></p>
            <div className="flex flex-wrap gap-2">
              {INDUSTRIES.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setIndustry(prev => prev === opt.value ? null : opt.value)}
                  className="flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-semibold border-2 transition-all"
                  style={{
                    borderColor: industry === opt.value ? "var(--color-brand)" : "var(--color-border)",
                    background:  industry === opt.value ? "var(--color-brand-light)" : "var(--color-surface)",
                    color:       industry === opt.value ? "var(--color-brand-text)" : "var(--color-text-secondary)",
                  }}
                >
                  <i className={opt.icon} />{opt.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <button
          onClick={create}
          disabled={!canCreate || loading}
          className="w-full bg-brand hover:bg-brand-hover disabled:bg-surface-hover disabled:text-faint text-on-brand py-4 rounded-2xl font-bold text-sm transition-all active:scale-[0.98] flex items-center justify-center gap-2"
        >
          {loading ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : "Continue"}
        </button>

        {!isAdditional && (
          <p className="text-center text-faint text-xs mt-5">
            That's everything we need for now. You can add your RC number, address, and other details anytime from Settings.
          </p>
        )}
      </div>
    </div>
  );
}
